import { createContext, ReactNode, useContext, useRef, useState } from 'react';
import { useMount, useUnmount } from 'ahooks';

const KeyboardContext = createContext(false);

function isEditableElement(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function KeyboardProvider({ children }: { children: ReactNode }) {
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const focusedEditableRef = useRef(false);
  const baselineHeightRef = useRef(
    window.visualViewport?.height ?? window.innerHeight,
  );
  const viewportSizeRef = useRef({
    height: window.visualViewport?.height ?? window.innerHeight,
    width: window.visualViewport?.width ?? window.innerWidth,
  });
  const animationFrameRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useMount(() => {
    const viewport = window.visualViewport ?? null;

    const syncKeyboardOpen = () => {
      const width = viewport?.width ?? window.innerWidth;
      const height = viewport?.height ?? window.innerHeight;
      const previousSize = viewportSizeRef.current;
      const widthChanged = Math.abs(width - previousSize.width) > 40;

      viewportSizeRef.current = { height, width };

      // Without an editable focus, treat keyboard as closed and reset the baseline.
      // Prevents sticky "open" state from browser chrome / orientation / partial restores.
      if (!focusedEditableRef.current) {
        baselineHeightRef.current = height;
        setKeyboardOpen(false);
        return;
      }

      if (widthChanged) {
        baselineHeightRef.current = height;
        setKeyboardOpen(false);
        return;
      }

      setKeyboardOpen(height < baselineHeightRef.current * 0.75);
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!isEditableElement(event.target)) return;
      focusedEditableRef.current = true;
      syncKeyboardOpen();
    };

    const onFocusOut = () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Defer so focus moving between editables does not briefly clear the flag.
      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = null;
        focusedEditableRef.current = isEditableElement(document.activeElement);
        syncKeyboardOpen();
      });
    };

    const onResize = () => {
      syncKeyboardOpen();
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    viewport?.addEventListener('resize', onResize);

    cleanupRef.current = () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      viewport?.removeEventListener('resize', onResize);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  });

  useUnmount(() => {
    cleanupRef.current?.();
  });

  return (
    <KeyboardContext.Provider value={keyboardOpen}>
      {children}
    </KeyboardContext.Provider>
  );
}

export const useKeyboardOpen = () => useContext(KeyboardContext);
